// WP6-B.5 — Marketing Site Images tab. Ported verbatim from the live
// Studio G4 pod's `AdminSiteImages.js`. Only mechanical adjustments:
//   • `../../lib/api` → `./marketingAdminApi`.
//   • `isCustom` broadened to `startsWith('/api/')` to match the new
//     `/api/public/files/serve/<uuid>` prefix (dispatch note under re-paste 3).
import React, { useState, useEffect, useRef } from "react";
import {
    adminGetSiteImages,
    adminUploadSiteImage,
    adminDeleteSiteImage,
} from "./marketingAdminApi";
import { Upload, Check, Image as ImageIcon, AlertCircle, RotateCcw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const IMAGE_SLOTS = [
    {
        key: "hero",
        label: "Hero Background",
        description: "The full-screen background image on the homepage hero section.",
        ratio: "16:9 (landscape)",
        recommended: "1920 x 1080 px minimum. Wider is better (2560 x 1440 ideal).",
        aspect: "aspect-[16/9]",
    },
    {
        key: "about",
        label: "About Section",
        description: "The image shown next to the About Us text on the homepage.",
        ratio: "4:3 (landscape)",
        recommended: "1200 x 900 px minimum.",
        aspect: "aspect-[4/3]",
    },
    {
        key: "portal_bg",
        label: "Client Portal Background",
        description:
            "Full-screen background on the client portal access code page. Also used on the Request a Quote page sidebar.",
        ratio: "3:4 (portrait) or 1:1",
        recommended: "1200 x 1600 px or 1400 x 1400 px.",
        aspect: "aspect-[3/4]",
    },
    {
        key: "portfolio_1",
        label: "Portfolio Placeholder 1",
        description: "Fallback image for the 1st and 4th project cards (wide cards) when no project images are uploaded.",
        ratio: "16:10 (landscape)",
        recommended: "1600 x 1000 px minimum.",
        aspect: "aspect-[16/10]",
    },
    {
        key: "portfolio_2",
        label: "Portfolio Placeholder 2",
        description: "Fallback for the 2nd project card (tall card) when no project images are uploaded.",
        ratio: "4:5 (portrait)",
        recommended: "800 x 1000 px minimum.",
        aspect: "aspect-[4/5]",
    },
    {
        key: "portfolio_3",
        label: "Portfolio Placeholder 3",
        description: "Fallback for the 3rd project card (tall card) when no project images are uploaded.",
        ratio: "4:5 (portrait)",
        recommended: "800 x 1000 px minimum.",
        aspect: "aspect-[4/5]",
    },
];

function resolveUrl(url) {
    if (!url) return "";
    if (url.startsWith("/api/")) return `${BACKEND}${url}`;
    return url;
}

function ImageSlotCard({ slot, currentUrl, defaultUrl, onUploaded, onReverted }) {
    const [uploading, setUploading] = useState(false);
    const [reverting, setReverting] = useState(false);
    const [justUploaded, setJustUploaded] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileRef = useRef(null);

    const handleFile = async (file) => {
        if (!file || !file.type.startsWith("image/")) return;
        setUploading(true);
        try {
            const result = await adminUploadSiteImage(slot.key, file);
            onUploaded(slot.key, result.url);
            setJustUploaded(true);
            setTimeout(() => setJustUploaded(false), 2000);
        } catch (e) {
            console.error("Upload error:", e);
        }
        setUploading(false);
    };

    const handleRevert = async () => {
        if (!window.confirm(`Revert "${slot.label}" to the default placeholder image?`)) return;
        setReverting(true);
        try {
            const result = await adminDeleteSiteImage(slot.key);
            onReverted(slot.key, result.default_url);
        } catch (e) {
            console.error("Revert error:", e);
        }
        setReverting(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const resolved = resolveUrl(currentUrl);
    const isCustom = currentUrl && currentUrl.startsWith("/api/");

    return (
        <div data-testid={`image-slot-${slot.key}`} className="border border-[#E5E5E5] bg-white">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-['Outfit'] text-base tracking-tight text-[#0A0A0A] font-medium">{slot.label}</h3>
                        <p className="text-xs text-[#525252] mt-1">{slot.description}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {justUploaded && <Check size={18} className="text-green-600" />}
                        {isCustom && (
                            <button
                                onClick={handleRevert}
                                disabled={reverting}
                                data-testid={`revert-${slot.key}`}
                                className="inline-flex items-center gap-1.5 text-xs text-[#525252] hover:text-[#0A0A0A] border border-[#E5E5E5] hover:border-[#0A0A0A] px-3 py-1.5 transition-colors disabled:opacity-50"
                                title="Revert to default placeholder"
                            >
                                <RotateCcw size={12} />
                                {reverting ? "Reverting..." : "Revert"}
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex gap-4 mb-4 text-xs">
                    <div className="border border-[#E5E5E5] px-3 py-1.5 bg-[#FAFAFA]">
                        <span className="text-[#525252]">Ratio: </span>
                        <span className="font-medium text-[#0A0A0A]">{slot.ratio}</span>
                    </div>
                    <div className="border border-[#E5E5E5] px-3 py-1.5 bg-[#FAFAFA]">
                        <span className="text-[#525252]">Min: </span>
                        <span className="font-medium text-[#0A0A0A]">{slot.recommended}</span>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div
                        className={`relative bg-[#FAFAFA] border border-[#E5E5E5] overflow-hidden ${slot.aspect} max-h-48`}
                    >
                        {resolved ? (
                            <>
                                <img src={resolved} alt={slot.label} className="w-full h-full object-cover" />
                                <span
                                    className={`absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold ${
                                        isCustom
                                            ? "bg-[#0A0A0A] text-white"
                                            : "bg-white/80 text-[#525252] border border-[#E5E5E5]"
                                    }`}
                                >
                                    {isCustom ? "Custom" : "Default"}
                                </span>
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon size={24} className="text-[#E5E5E5]" />
                            </div>
                        )}
                    </div>
                    <div
                        className={`border-2 border-dashed flex flex-col items-center justify-center p-4 cursor-pointer transition-colors ${slot.aspect} max-h-48 ${
                            dragActive ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] hover:border-[#9A8B7E]"
                        }`}
                        onDrop={handleDrop}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragActive(true);
                        }}
                        onDragLeave={() => setDragActive(false)}
                        onClick={() => fileRef.current?.click()}
                    >
                        <Upload size={20} className="text-[#9A8B7E] mb-2" />
                        <p className="text-xs text-[#525252] text-center">
                            {uploading ? "Uploading..." : "Drop image or click"}
                        </p>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid={`upload-${slot.key}`}
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            disabled={uploading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function MarketingSiteImages() {
    const [images, setImages] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminGetSiteImages()
            .then((data) => {
                setImages(data.images || {});
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    const handleUploaded = (key, url) => {
        setImages((prev) => ({ ...prev, [key]: url }));
    };
    const handleReverted = (key, defaultUrl) => {
        setImages((prev) => ({ ...prev, [key]: defaultUrl }));
    };

    return (
        <div data-testid="admin-site-images-page" className="p-8 md:p-12">
            <div className="mb-10">
                <h1 className="font-['Outfit'] text-2xl tracking-tight text-[#0A0A0A]">Site Images</h1>
                <p className="text-sm text-[#525252] mt-1">Manage the images displayed across the website</p>
            </div>
            <div className="flex items-start gap-3 border border-[#E5E5E5] bg-[#FAFAFA] p-4 mb-8 max-w-3xl">
                <AlertCircle size={18} className="text-[#9A8B7E] shrink-0 mt-0.5" />
                <div className="text-xs text-[#525252] leading-relaxed">
                    <p className="font-medium text-[#0A0A0A] mb-1">Image Guidelines</p>
                    <p>
                        Use high-quality photographs in JPG or PNG format. Each slot shows the required aspect ratio and
                        minimum dimensions. Images are automatically served from object storage — changes appear
                        immediately on the live site.
                    </p>
                </div>
            </div>
            {loading ? (
                <p className="text-sm text-[#525252]">Loading...</p>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
                    {IMAGE_SLOTS.map((slot) => (
                        <ImageSlotCard
                            key={slot.key}
                            slot={slot}
                            currentUrl={images[slot.key]}
                            onUploaded={handleUploaded}
                            onReverted={handleReverted}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
